import { defineStore } from 'pinia';
import api from '@/services/api';
import { useAuthStore } from './auth';

// Data is considered stale after 5 minutes
const STALE_TIME = 5 * 60 * 1000;

export const useInvitationsStore = defineStore('invitations', {
	state: () => ({
		invitations: [],
		loading: false,
		error: null,
		lastFetched: null
	}),

	getters: {
		isStale: (state) => !state.lastFetched || Date.now() - state.lastFetched > STALE_TIME,
		hasData: (state) => state.invitations.length > 0,

		// Invitations sent to current user (pending acceptance)
		myPendingInvitations(state) {
			const authStore = useAuthStore();
			const userEmail = authStore.userEmail;
			const now = new Date();

			return state.invitations.filter((inv) => inv.inviteeEmail === userEmail && !inv.cancelled && !inv.acceptedBy && new Date(inv.expires) > now);
		},

		// All invitations for a specific circle (for leaders to see)
		invitationsByCircle: (state) => (circleId) => {
			return state.invitations.filter((inv) => {
				const invCircleId = inv.circle?.id || inv.circle;
				return invCircleId === circleId;
			});
		},

		// Pending invitations for a specific circle (not cancelled, not accepted, not expired)
		pendingInvitationsForCircle: (state) => (circleId) => {
			const now = new Date();
			return state.invitations.filter((inv) => {
				const invCircleId = inv.circle?.id || inv.circle;
				return invCircleId === circleId && !inv.cancelled && !inv.acceptedBy && new Date(inv.expires) > now;
			});
		}
	},

	actions: {
		async fetchInvitations() {
			this.loading = true;
			this.error = null;
			try {
				const response = await api.getInvitations();
				this.invitations = response.data;
				this.lastFetched = Date.now();
				return response.data;
			} catch (error) {
				this.error = error.message || 'Failed to fetch invitations';
				console.error('Error fetching invitations:', error);
				throw error;
			} finally {
				this.loading = false;
			}
		},

		// Smart fetch - only fetches if data is missing or stale
		async ensureLoaded(force = false) {
			// Skip if already loading
			if (this.loading) return this.invitations;

			// Skip if we have fresh data and not forcing
			if (!force && this.hasData && !this.isStale) {
				return this.invitations;
			}

			return this.fetchInvitations();
		},

		async createInvitation({ circleId, inviteeName, inviteeEmail }) {
			this.loading = true;
			this.error = null;
			try {
				const invitationData = {
					circle: { id: circleId },
					inviteeEmail
				};
				if (inviteeName) {
					invitationData.inviteeName = inviteeName;
				}
				const response = await api.createInvitation(invitationData);
				this.invitations.push(response.data);
				return response.data;
			} catch (error) {
				this.error = error.message || 'Failed to create invitation';
				console.error('Error creating invitation:', error);
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async acceptInvitation(invitationId) {
			this.loading = true;
			this.error = null;
			try {
				const authStore = useAuthStore();
				const response = await api.updateInvitation(invitationId, {
					acceptedBy: authStore.userId
				});

				// Update in local state
				const index = this.invitations.findIndex((inv) => inv.id === invitationId);
				if (index !== -1) {
					this.invitations.splice(index, 1, response.data);
				}

				return response.data;
			} catch (error) {
				this.error = error.message || 'Failed to accept invitation';
				console.error('Error accepting invitation:', error);
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async cancelInvitation(invitationId) {
			this.loading = true;
			this.error = null;
			try {
				await api.deleteInvitation(invitationId);

				// Update in local state
				const index = this.invitations.findIndex((inv) => inv.id === invitationId);
				if (index !== -1) {
					this.invitations[index].cancelled = true;
				}

				return true;
			} catch (error) {
				this.error = error.message || 'Failed to cancel invitation';
				console.error('Error cancelling invitation:', error);
				throw error;
			} finally {
				this.loading = false;
			}
		}
	}
});
